import React from 'react';
import { AssignmentAssociate } from '../models';
import AssociateNameRow from './AssociateNameRow';

export interface IndirectAssociateRowProps {
  associate: AssignmentAssociate;
  processPathId: string;
  /**
   * Fires when the user activates the per-row remove control on this
   * indirect-path associate.
   *
   * Optional: not used when `readOnly` is `true`, since the per-row
   * interactive control is suppressed in that mode and there is no
   * caller for it.
   */
  onRemove?: (associateId: string, processPathId: string) => void;
  /**
   * When `true`, the indirect-associate row renders in informational
   * mode for the Published Assignment Screen: the underlying
   * `AssociateNameRow` is rendered with `showCheckbox={false}` so no
   * per-row interactive control appears. The associate name, avatar,
   * and warning/checkmark indicators render exactly as in editable
   * mode.
   *
   * Defaults to `false`, in which case behavior is bit-for-bit
   * identical to the editable Assignments page.
   */
  readOnly?: boolean;
}

/**
 * Row representing a single associate assigned to an indirect process path
 * (e.g. Problem solve). Uses the shared AssociateNameRow component for
 * consistent styling with the rest of the assignments screen.
 *
 * The checkbox is unchecked by default; selecting it will be used for the
 * move flow in a future iteration. The checkmark on the right indicates
 * the associate is currently assigned to this path.
 *
 * When `readOnly` is `true` (Published Assignment Screen): the per-row
 * checkbox is suppressed via `showCheckbox={false}`. All data (name,
 * avatar, warning indicator, assignment-checkmark icon) renders exactly
 * as in editable mode.
 */
const IndirectAssociateRow: React.FC<IndirectAssociateRowProps> = ({
  associate,
  processPathId,
  readOnly = false,
}) => {
  return (
    <AssociateNameRow
      associateName={associate.associateName}
      associateAlias={associate.associateAlias}
      showCheckbox={!readOnly}
      checkboxChecked={false}
      showCheckmark={true}
      hasWarning={associate.hasWarning}
      data-testid={`indirect-associate-row-${associate.associateId}`}
    />
  );
};

export default IndirectAssociateRow;
